<template>
  <div
    ref="menuContainer"
    class="menu-container"
    :class="{'menu-container_hidden': !isVisible, 'menu-container_with-system': hasSystem}"
    :style="containerStyle"
  >
    <sidebar-menu
        :menu="menu"
        :collapsed="isCollapsed"
        :width="expandedWidth"
        :width-collapsed="collapsedWidth"
        :hide-toggle="$store.getters.isMobile"
        relative
        @toggle-collapse="onToggleCollapse"
    >
        <span slot="dropdown-icon" class="vsm-chevron"></span>
        <span slot="toggle-icon" class="vsm-chevron vsm-chevron_toggle"></span>
    </sidebar-menu>
  </div>
</template>

<script>
import { SidebarMenu } from 'vue-sidebar-menu'

// Ikony trzymamy jako require, bo pozycje menu są zwykłymi obiektami JS —
// url-loader zamienia ścieżkę na URL (albo data URI dla małych SVG) w buildzie.
const ICONS = {
    alarms: require('@/assets/img/Menu/Alarms.svg'),
    animals: require('@/assets/img/Systems/Animals_Cow.svg'),
    chart: require('@/assets/img/Menu/chart.svg'),
    cloud: require('@/assets/img/Menu/cloud.svg'),
    contact: require('@/assets/img/Menu/Contact.svg'),
    dashboard: require('@/assets/img/Menu/Dashboard.svg'),
    device: require('@/assets/img/Systems/Device.svg'),
    faq: require('@/assets/img/Menu/FAQ.svg'),
    mail: require('@/assets/img/Menu/Mail.svg'),
    maps: require('@/assets/img/Menu/Maps.svg'),
    roughage: require('@/assets/img/Systems/Roughage.svg'),
    sensors: require('@/assets/img/Menu/Sensors.svg'),
    service: require('@/assets/img/Menu/Service.svg'),
    settings: require('@/assets/img/Menu/Settings.svg'),
    sorting: require('@/assets/img/Systems/Sorting.svg'),
    statistics: require('@/assets/img/Systems/Statistics.svg'),
    systems: require('@/assets/img/Menu/Systems.svg'),
    ticketsNew: require('@/assets/img/Systems/MessageNewMailSmall.svg'),
    ticketsNone: require('@/assets/img/Systems/MessageSmall.svg'),
    todo: require('@/assets/img/Menu/Todo.svg'),
    todoNone: require('@/assets/img/Menu/Todo-none.svg'),
    todoNull: require('@/assets/img/Menu/Todo-null.svg'),
    users: require('@/assets/img/Menu/Users.svg')
}

const COLLAPSED_STORAGE_KEY = 'menuCollapsed'

export default {
    components: {
        SidebarMenu
    },
    props: {
        isVisible: {
            type: Boolean,
            default: true,
            required: true
        },
        absolute: {
            type: Boolean,
            default: false,
            required: false
        }
    },
    data() {
        return {
            collapsed: localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true',
            expandedWidth: '250px',
            collapsedWidth: '65px'
        }
    },
    computed: {
        notUser() {
            return this.$store.getters.isAdmin || this.$store.getters.isService
        },
        // Na mobile menu wysuwa się nad treść i zawsze pokazuje pełne pozycje —
        // tryb ikon miałby tam sens tylko razem z toggle, który jest ukryty.
        isCollapsed() {
            return this.collapsed && !this.$store.getters.isMobile
        },
        containerWidth() {
            if (!this.isVisible) return '0px'
            return this.isCollapsed ? this.collapsedWidth : this.expandedWidth
        },
        // Szerokości wystawiamy też jako zmienne CSS, bo z nich liczona jest
        // szerokość wysuwki pokazywanej przy hoverze w trybie ikon.
        containerStyle() {
            return {
                position: this.absolute ? 'absolute' : 'relative',
                width: this.containerWidth,
                '--menu-width': this.expandedWidth,
                '--menu-width-collapsed': this.collapsedWidth
            }
        },
        hasSystem() {
            return this.$route.params.systemName !== undefined
        },
        // Pozycje systemowe pokazujemy tylko wtedy, gdy jakiś system jest otwarty.
        // Cała sekcja to jedna gałąź podpięta pod nazwę systemu, żeby było widać,
        // że to kontekst systemu, a nie kolejne pozycje menu głównego; params
        // przekazujemy w całości, bo trasy systemState wymagają :systemName i :systemId.
        //
        // alias trzyma gałąź rozwiniętą także na podstronach, których nie ma w menu
        // (np. szczegóły problemu) — bez tego sidebar uznałby sekcję za nieaktywną
        // i zwinął ją użytkownikowi pod ręką.
        systemMenu() {
            const params = this.$route.params
            if (!this.hasSystem) return []

            return [{
                title: params.systemName,
                class: 'vsm--link_system',
                alias: [
                    '/systemState/:systemName/:systemId',
                    '/systemState/:systemName/:systemId/(.*)',
                    '/devices/:systemName/:systemId'
                ],
                icon: { element: 'img', attributes: { src: ICONS.sensors, alt: '' } },
                child: this.systemMenuChildren(params)
            }]
        },
        mainMenu() {
            const isAdmin = this.$store.getters.isAdmin

            return [
                {
                    ...this.item('Systems', 'systems', ICONS.systems),
                    child: [
                        this.item('Map', 'map'),
                        this.item('Dashboard', 'dashboard'),
                        !this.$store.getters.isUser && this.item('Weights', 'weights')
                    ].filter(Boolean)
                },
                isAdmin
                    ? {
                        ...this.item('Users', 'users', ICONS.users),
                        child: [
                            this.item('Invitations', 'invitations'),
                            this.item('Contacts', 'contacts')
                        ]
                    }
                    : this.$store.getters.isService && this.item('Contacts', 'contacts', ICONS.contact),
                this.notUser && this.item('Alarms', 'alarms', ICONS.alarms),
                this.notUser && {
                    ...this.item('Service', 'visits', ICONS.service),
                    child: [
                        this.item('Visits', 'visitsList'),
                        this.item('Problems', 'visitsProblems'),
                        this.item('Solutions', 'visitsSolutions')
                    ]
                },
                isAdmin && this.item('Cloud', 'cloud', ICONS.cloud),
                this.item('FAQ', 'faq', ICONS.faq),
                isAdmin && {
                    ...this.item('Software', 'software', ICONS.cloud),
                    child: [
                        this.item('SoftwareDownloads', 'software'),
                        this.item('SoftwareVersions', 'softwareManagement'),
                        this.item('Requirements', 'softwareRequirements')
                    ]
                },
                isAdmin && this.item('Backups', 'backups', ICONS.cloud),
                isAdmin && this.item('ScheduledReports', 'scheduledReports', ICONS.mail),
                isAdmin && this.item('ApiUsage', 'apiUsage', ICONS.chart)
            ].filter(Boolean)
        },
        menu() {
            return [...this.systemMenu, ...this.mainMenu]
        },
        tasksIcon() {
            const hasTasks = this.$store.getters.hasTasks
            if (hasTasks == null) return ICONS.todoNull
            return hasTasks ? ICONS.todo : ICONS.todoNone
        },
        ticketsIcon() {
            return this.$store.getters.hasTickets ? ICONS.ticketsNew : ICONS.ticketsNone
        }
    },
    methods: {
        systemMenuChildren(params) {
            return [
                this.item('General', 'settingsGeneral', ICONS.settings, params),
                this.item('Tasks', 'systemsTasks', this.tasksIcon, params),
                this.item('Animals', 'systemAnimals', ICONS.animals, params),
                this.item('Devices', 'settingsDevices', ICONS.device, params),
                this.notUser && this.item('DeviceAlarms', 'settingsAlarms', ICONS.alarms, params),
                this.notUser && this.item('General Alarms', 'systemsGeneralAlarms', ICONS.alarms, params),
                this.item('Roughage', 'settingsRoughage', ICONS.roughage, params),
                this.item('Sorting', 'settingsSorting', ICONS.sorting, params),
                this.item('Statistics', 'settingsStatistics', ICONS.statistics, params),
                this.notUser && this.item('Visits', 'systemVisits', ICONS.service, params),
                this.notUser && this.item('Problems', 'settingsProblems', ICONS.service, params),
                this.notUser && this.item('Settings', 'systemSettings', ICONS.settings, params),
                this.notUser && this.item('Tickets', 'systemTickets', this.ticketsIcon, params),
                this.notUser && this.item('Notifications', 'systemNotifications', ICONS.mail, params)
            ].filter(Boolean)
        },
        // Tytuł tłumaczymy tutaj, bo komponent sidebara renderuje item.title
        // jako czysty tekst. Klucze zostają te same, co w starym szablonie.
        item(titleKey, routeName, icon, params) {
            return {
                href: params ? { name: routeName, params: params } : { name: routeName },
                title: this.$t(titleKey),
                icon: icon ? { element: 'img', attributes: { src: icon, alt: '' } } : undefined
            }
        },
        onToggleCollapse(collapsed) {
            this.collapsed = collapsed
            localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed)
        }
    }
}
</script>

<style scoped>
.menu-container {
    transition: 0.3s ease-out;
    /* Kolumna, żeby sidebar dostał dokładnie wysokość kontenera, a nie
       wypchnął zawartość poza ekran. */
    display: flex;
    flex-direction: column;
    z-index: 4;
    background-color: var(--sidebar-bg);
    height: calc(100vh - 50px);
    width: 250px;
    /* Scroll ma sam sidebar (.vsm--scroll-wrapper). Kontener nie może
       przycinać zawartości, bo w trybie ikon wysuwka z nazwą pozycji
       wychodzi poza jego szerokość. */
    overflow: visible;
}

/* Schowane menu musi przycinać — inaczej etykiety wystają nad treść. */
.menu-container_hidden {
    overflow: hidden;
}

@media only screen and (max-width: 700px) {
    .menu-container {
        position: absolute !important;
    }
}
</style>

<!--
  Styl sidebara jest globalny (nie scoped), bo klasy vsm--* renderuje
  komponent z paczki. Kolory idą przez zmienne, więc podmiana palety na
  brandowy gradient z service-ui to zmiana dwóch deklaracji na .v-sidebar-menu.
-->
<style>
.menu-container .v-sidebar-menu {
    --menu-bg: var(--sidebar-bg);
    --menu-fg: #e5f4f7;
    --menu-accent: rgba(255, 255, 255, 0.14);
    --menu-accent-fg: #ffffff;
    --menu-border: rgba(255, 255, 255, 0.15);
    --menu-muted: rgba(229, 244, 247, 0.55);

    background-color: var(--menu-bg);
    color: var(--menu-fg);
    max-width: 100%;
    /* Menu wypełnia kontener i przewija się w środku — bez tego jego zawartość
       wychodzi poza ekran i przewijaniem zajmuje się okno przeglądarki. */
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
}

/* Sidebar dzieli wysokość na przewijaną listę i przycisk zwijania. Bez
   min-height lista rozpycha flexa własną wysokością i wychodzi poza ekran. */
.menu-container .v-sidebar-menu .vsm--scroll-wrapper {
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.28) transparent;
}

/* Globalny scrollbar aplikacji jest jasny — na ciemnym menu wyglądałby jak
   doklejony pasek przeglądarki, więc tutaj ma własny, stonowany wygląd. */
.menu-container .v-sidebar-menu .vsm--scroll-wrapper::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

.menu-container .v-sidebar-menu .vsm--scroll-wrapper::-webkit-scrollbar-track {
    background: transparent;
    box-shadow: none;
    border: none;
}

.menu-container .v-sidebar-menu .vsm--scroll-wrapper::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.22);
    border-radius: 3px;
}

.menu-container .v-sidebar-menu .vsm--scroll-wrapper::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.38);
}

.menu-container .v-sidebar-menu .vsm--link {
    margin: 2px 8px;
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 20px;
    color: var(--menu-fg);
}

.menu-container .v-sidebar-menu .vsm--link:hover,
.menu-container .v-sidebar-menu .vsm--link_hover {
    background-color: var(--menu-accent);
    color: var(--menu-accent-fg);
}

.menu-container .v-sidebar-menu .vsm--link_active,
.menu-container .v-sidebar-menu .vsm--link_exact-active {
    background-color: var(--menu-accent);
    color: var(--menu-accent-fg);
    font-weight: 500;
}

/*
 * Motyw paczki (vue-sidebar-menu.css) maluje aktywne i rozwinięte pozycje na
 * niebiesko (#4285f4): tło wiersza, kwadrat pod ikoną i pasek box-shadow po
 * lewej. Jego reguły są mocniejsze od powyższych, więc każdą powtarzamy tu
 * z prefiksem .menu-container — wygrywa niezależnie od kolejności CSS.
 */
.menu-container .v-sidebar-menu .vsm--link_level-1.vsm--link_active,
.menu-container .v-sidebar-menu .vsm--link_level-1.vsm--link_exact-active {
    box-shadow: none;
}

/* Pozycja z rozwiniętą listą nie ma tła — inaczej cała gałąź wygląda na aktywną. */
.menu-container .v-sidebar-menu.vsm_expanded .vsm--item_open .vsm--link_level-1 {
    background-color: transparent;
}

.menu-container .v-sidebar-menu.vsm_expanded .vsm--item_open .vsm--link_level-1:hover,
.menu-container .v-sidebar-menu.vsm_expanded .vsm--item_open .vsm--link_level-1.vsm--link_exact-active {
    background-color: var(--menu-accent);
}

.menu-container .v-sidebar-menu.vsm_expanded .vsm--item_open .vsm--link_level-1 .vsm--icon,
.menu-container .v-sidebar-menu.vsm_collapsed .vsm--link_level-1.vsm--link_hover .vsm--icon,
.menu-container .v-sidebar-menu.vsm_collapsed .vsm--link_level-1:hover .vsm--icon {
    background-color: transparent;
}

.menu-container .v-sidebar-menu .vsm--title {
    margin-left: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* .vsm--link w selektorze, żeby przebić 30px i ciemne tło z .vsm--link_level-1 .vsm--icon paczki. */
.menu-container .v-sidebar-menu .vsm--link .vsm--icon {
    width: 22px;
    height: 22px;
    line-height: 22px;
    background-color: transparent;
    object-fit: contain;
    flex-shrink: 0;
}

/* W trybie ikon wiersz zawiera samą ikonę — centrujemy ją w podświetleniu,
   zamiast zostawiać przy lewym paddingu z odstępem pod nieobecny tytuł. */
.menu-container .v-sidebar-menu.vsm_collapsed .vsm--link_level-1 {
    justify-content: center;
}

.menu-container .v-sidebar-menu.vsm_collapsed .vsm--link_level-1 .vsm--icon {
    margin-right: 0;
}

.menu-container .v-sidebar-menu .vsm--header {
    padding: 14px 18px 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--menu-muted);
    overflow: hidden;
    text-overflow: ellipsis;
}

/*
 * Sekcja systemu to osobny blok na górze menu: ciemniejsze tło i kreska pod
 * spodem oddzielają kontekst otwartego systemu od stałego menu aplikacji.
 */
.menu-container_with-system .v-sidebar-menu > .vsm--scroll-wrapper > .vsm--list > .vsm--item:first-child {
    background-color: rgba(0, 0, 0, 0.16);
    border-bottom: 1px solid var(--menu-border);
    padding: 4px 0 8px;
    margin-bottom: 8px;
}

.menu-container .v-sidebar-menu .vsm--link_system {
    font-size: 15px;
    font-weight: 600;
    color: var(--menu-accent-fg);
}

.menu-container .v-sidebar-menu .vsm--link_system:hover {
    background-color: var(--menu-accent);
}

/* Poddrzewo: pionowa kreska po lewej, jak w sidebarze shadcn. */
.menu-container .v-sidebar-menu .vsm--dropdown > .vsm--list {
    padding: 2px 0 2px 0;
    margin-left: 26px;
    border-left: 1px solid var(--menu-border);
    background-color: transparent;
}

.menu-container .v-sidebar-menu .vsm--dropdown .vsm--link {
    font-size: 13px;
    padding-left: 14px;
}

/*
 * Tryb ikon: po najechaniu paczka wysuwa obok sidebara panel z nazwą pozycji
 * i jej poddrzewem. Szerokość liczy z clientWidth kontenera (czyli 65px),
 * dlatego wymuszamy tu pełną szerokość menu; !important bije styl inline.
 */
.menu-container .v-sidebar-menu .vsm--mobile-item {
    width: calc(var(--menu-width-collapsed) + var(--menu-width)) !important;
    max-width: calc(var(--menu-width-collapsed) + var(--menu-width)) !important;
}

.menu-container .v-sidebar-menu .vsm--mobile-item .vsm--link_mobile-item,
.menu-container .v-sidebar-menu .vsm--dropdown_mobile-item,
.menu-container .v-sidebar-menu .vsm--mobile-bg {
    background-color: var(--menu-bg);
    color: var(--menu-fg);
}

.menu-container .v-sidebar-menu .vsm--dropdown_mobile-item {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.menu-container .v-sidebar-menu .vsm--toggle-btn {
    height: 42px;
    background-color: transparent;
    color: var(--menu-muted);
    border-top: 1px solid var(--menu-border);
}

.menu-container .v-sidebar-menu .vsm--toggle-btn:hover {
    color: var(--menu-accent-fg);
    background-color: var(--menu-accent);
}

/* Paczka wstawia w strzałkę i toggle glify Font Awesome, których projekt nie ma. */
.menu-container .v-sidebar-menu .vsm--toggle-btn:after {
    content: none;
}

.menu-container .v-sidebar-menu .vsm--arrow {
    width: 20px;
}

.vsm-chevron {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    transform: rotate(-45deg);
    vertical-align: middle;
}

.vsm-chevron_toggle {
    transform: rotate(135deg);
}

.menu-container .v-sidebar-menu.vsm_collapsed .vsm-chevron_toggle {
    transform: rotate(-45deg);
}
</style>



// WEBPACK FOOTER //
// src/components/Main/Menu/NavigationMenu.vue